/**
 * Audio Recorder for WebSocket Communication
 * Handles microphone input, processing, and sending audio chunks
 */

const AUDIO_CONFIG = {
  inputSampleRate: 48000,
  outputSampleRate: 24000, // OpenAI Realtime API uses 24kHz for PCM16 (both input and output)
  channelCount: 1,
  chunkDuration: 100,
  voiceThreshold: 0.015, // Very sensitive - captures quiet speech and natural pauses
  silenceDuration: 800, // OPTIMIZED: Reduced from 1800ms to 800ms for faster response
  silenceCheckInterval: 50, // Check every 50ms instead of 100ms for faster detection
};

export class AudioRecorder {
  constructor(callbacks) {
    this.callbacks = callbacks;
    this.audioContext = null;
    this.mediaStream = null;
    this.processor = null;
    this.audioBuffer = [];
    this.completeAudioBuffer = []; // Store ALL audio for batch sending
    this.isRecording = false;
    this.chunkCounter = 0;
    this.lastVoiceTime = null;
    this.silenceCheckInterval = null;
    this.hasDetectedVoice = false; // Track if we've detected voice at all
    this.isSending = false; // Prevent double-sending
  }

  async start() {
    if (this.isRecording) {
      console.warn('[AudioRecorder] ⚠️ Already recording');
      return;
    }

    try {
      console.log('[AudioRecorder] 🎙️ Starting...');

      // Get media stream if we don't have one
      if (!this.mediaStream) {
        console.log('[AudioRecorder] 📡 Requesting microphone access...');
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: AUDIO_CONFIG.channelCount,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        console.log('[AudioRecorder] ✓ Microphone access granted');
      }

      // Create audio context if we don't have one
      if (!this.audioContext) {
        console.log('[AudioRecorder] 🔧 Creating AudioContext...');
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        console.log('[AudioRecorder] ✓ AudioContext created');
      }

      // Resume context if suspended
      if (this.audioContext.state === 'suspended') {
        console.log('[AudioRecorder] ▶️ Resuming AudioContext...');
        await this.audioContext.resume();
      }

      // Create new processor
      const source = this.audioContext.createMediaStreamSource(this.mediaStream);
      
      // Increase buffer size for better quality and performance
      const bufferSize = 16384; // Maximum size (was 4096)
      this.processor = this.audioContext.createScriptProcessor(
        bufferSize,
        AUDIO_CONFIG.channelCount,
        AUDIO_CONFIG.channelCount
      );

      this.processor.onaudioprocess = (event) => {
        this.processAudioChunk(event);
      };

      source.connect(this.processor);
      this.processor.connect(this.audioContext.destination);

      this.isRecording = true;
      this.chunkCounter = 0;
      this.lastVoiceTime = Date.now();
      this.hasDetectedVoice = false;
      this.completeAudioBuffer = []; // Reset buffer on start
      this.isSending = false; // Reset sending flag
      
      // Start checking for silence
      this.startSilenceDetection();
      
      console.log('[AudioRecorder] ✅ Started successfully - buffering audio locally');

    } catch (error) {
      console.error('[AudioRecorder] ❌ Failed to start:', error);
      throw error;
    }
  }

  processAudioChunk(event) {
    if (!this.isRecording) return;

    const inputData = event.inputBuffer.getChannelData(0);
    
    // Apply noise gate to reduce ambient noise
    const gatedData = this.applyNoiseGate(inputData);
    
    // Voice activity detection on gated audio
    const rms = this.calculateRMS(gatedData);
    const voiceDetected = rms > AUDIO_CONFIG.voiceThreshold;
    
    // Update last voice time if voice is detected
    if (voiceDetected) {
      const wasSilent = !this.hasDetectedVoice || (Date.now() - this.lastVoiceTime) > 1000;
      if (wasSilent) {
        console.log(`[AudioRecorder] 🎤 SPEECH DETECTED (RMS: ${rms.toFixed(4)}, threshold: ${AUDIO_CONFIG.voiceThreshold})`);
      }
      this.lastVoiceTime = Date.now();
      this.hasDetectedVoice = true;
    }
    
    this.callbacks.onVoiceDetection(voiceDetected);

    // Only add audio to buffer AFTER first voice is detected
    // This skips initial silence before user starts speaking
    if (this.hasDetectedVoice) {
      this.completeAudioBuffer.push(...gatedData);
      
      // Log progress every 2 seconds worth of audio
      if (this.completeAudioBuffer.length % (this.audioContext.sampleRate * 2) < gatedData.length) {
        const durationSeconds = (this.completeAudioBuffer.length / this.audioContext.sampleRate).toFixed(1);
        console.log(`[AudioRecorder] 📊 Buffered ${durationSeconds}s of audio (${this.completeAudioBuffer.length} samples)`);
      }
    }
    // Else: Voice not detected yet - skip this chunk (initial silence)
  }

  applyNoiseGate(samples, threshold = 0.01) {
    // Noise gate: attenuate samples below threshold to reduce ambient noise
    const gated = new Float32Array(samples.length);
    for (let i = 0; i < samples.length; i++) {
      const amplitude = Math.abs(samples[i]);
      if (amplitude < threshold) {
        // Attenuate quiet samples (ambient noise)
        gated[i] = samples[i] * 0.1;
      } else {
        // Keep loud samples (speech)
        gated[i] = samples[i];
      }
    }
    return gated;
  }

  startSilenceDetection() {
    // OPTIMIZED: Check every 50ms instead of 100ms for faster response
    this.silenceCheckInterval = setInterval(() => {
      if (!this.isRecording || this.isSending) {
        return; // Don't check if not recording or already sending
      }

      if (!this.lastVoiceTime) {
        return; // Haven't detected voice yet, keep waiting
      }

      const timeSinceVoice = Date.now() - this.lastVoiceTime;
      
      // OPTIMIZED: Only auto-send if we've detected voice at least once and then had silence
      // Reduced from 1800ms to 800ms for much faster response
      if (this.hasDetectedVoice && timeSinceVoice > AUDIO_CONFIG.silenceDuration) {
        console.log('╔═══════════════════════════════════════════════════════╗');
        console.log('║ [AudioRecorder] 🔇 SILENCE DETECTED - SENDING AUDIO  ║');
        console.log('╠═══════════════════════════════════════════════════════╣');
        console.log(`║ Silence Duration: ${timeSinceVoice}ms (threshold: ${AUDIO_CONFIG.silenceDuration}ms)`.padEnd(56) + '║');
        console.log(`║ Has Voice: ${this.hasDetectedVoice}`.padEnd(56) + '║');
        console.log(`║ Buffer Duration: ${(this.completeAudioBuffer.length / this.audioContext.sampleRate).toFixed(2)}s`.padEnd(56) + '║');
        console.log('╚═══════════════════════════════════════════════════════╝');
        
        clearInterval(this.silenceCheckInterval); // Stop checking
        this.silenceCheckInterval = null;
        this.sendCompleteAudio();
        this.callbacks.onSilenceDetected?.();
      }
    }, AUDIO_CONFIG.silenceCheckInterval);
  }

  sendCompleteAudio() {
    if (this.isSending) {
      console.warn('[AudioRecorder] ⚠️ Already sending audio, skipping duplicate send');
      return;
    }
    
    if (this.completeAudioBuffer.length === 0) {
      console.warn('[AudioRecorder] ⚠️ No audio to send - buffer is empty');
      return;
    }

    this.isSending = true; // Set flag to prevent double-sending

    try {
      const totalDuration = (this.completeAudioBuffer.length / this.audioContext.sampleRate).toFixed(2);
      console.log(`[AudioRecorder] 📊 Processing ${totalDuration}s audio (${this.completeAudioBuffer.length} samples)`);
      
      // Convert to Float32Array
      let float32Data = new Float32Array(this.completeAudioBuffer);
      
      // Clean and preprocess audio
      float32Data = this.preprocessAudio(float32Data);
      
      // Resample to target rate
      const resampledData = this.resample(
        float32Data,
        this.audioContext.sampleRate,
        AUDIO_CONFIG.outputSampleRate
      );

      // Convert to PCM16
      const pcm16 = this.float32ToPCM16(resampledData);

      // Convert to base64
      const base64Audio = this.arrayBufferToBase64(pcm16.buffer);
      console.log(`[AudioRecorder] ✅ Sending optimized audio: ${base64Audio.length} chars, ${totalDuration}s duration`);
      
      // Send entire audio as one chunk
      this.callbacks.onAudioChunk(base64Audio);
      
      // Clear buffer after sending (important - don't send twice!)
      this.completeAudioBuffer = [];
      this.hasDetectedVoice = false; // Reset for next turn
      this.lastVoiceTime = Date.now(); // Reset timer
      this.isSending = false; // Reset flag
      
      console.log('╔═══════════════════════════════════════════════════════╗');
      console.log('║ [AudioRecorder] � READY FOR NEXT TURN               ║');
      console.log('╠═══════════════════════════════════════════════════════╣');
      console.log(`║ Buffer cleared: ${this.completeAudioBuffer.length} samples`.padEnd(56) + '║');
      console.log(`║ Voice detected: ${this.hasDetectedVoice}`.padEnd(56) + '║');
      console.log(`║ Is recording: ${this.isRecording}`.padEnd(56) + '║');
      console.log(`║ Is sending: ${this.isSending}`.padEnd(56) + '║');
      console.log('╚═══════════════════════════════════════════════════════╝');
      
      // Restart silence detection for next user turn (continuous hands-free mode)
      if (this.isRecording && !this.silenceCheckInterval) {
        console.log(`[AudioRecorder] 🔄 Restarting silence detection for next turn`);
        this.startSilenceDetection();
      }
      
    } catch (error) {
      console.error('[AudioRecorder] ❌ Failed to send complete audio:', error);
      console.error('Stack trace:', error.stack);
      this.isSending = false; // Reset flag on error
    }
  }

  preprocessAudio(float32Data) {
    // OPTIMIZED: Streamlined preprocessing with reduced logging
    
    // 1. Apply noise reduction (spectral subtraction approximation)
    const noiseReduced = this.reduceNoise(float32Data);
    
    // 2. Trim silence from beginning and end
    const trimmed = this.trimSilence(noiseReduced);
    
    // 3. Normalize volume
    const normalized = this.normalizeAudio(trimmed);
    
    // 4. Apply high-pass filter to remove DC offset and low-frequency noise
    const filtered = this.highPassFilter(normalized);
    
    console.log(`[AudioRecorder] 🧹 Preprocessed: ${float32Data.length} → ${filtered.length} samples`);
    
    return filtered;
  }

  reduceNoise(samples) {
    // Simple noise reduction: smooth out very quiet samples
    const reduced = new Float32Array(samples.length);
    const noiseThreshold = 0.008;
    
    for (let i = 0; i < samples.length; i++) {
      const amplitude = Math.abs(samples[i]);
      
      if (amplitude < noiseThreshold) {
        // Attenuate noise floor
        reduced[i] = samples[i] * 0.2;
      } else {
        // Keep speech signal
        reduced[i] = samples[i];
      }
    }
    
    return reduced;
  }

  trimSilence(samples, threshold = 0.005) {
    // Note: Initial silence is already removed by only buffering after first voice detection
    // This function primarily removes trailing silence after user stops speaking
    
    // Find last non-silent sample (trim trailing silence)
    let end = samples.length;
    for (let i = samples.length - 1; i >= 0; i--) {
      if (Math.abs(samples[i]) > threshold) {
        // Keep only 50ms of trailing silence (1200 samples at 24kHz)
        end = Math.min(samples.length, i + 1200);
        break;
      }
    }
    
    // No need to trim beginning - we never buffer initial silence
    return samples.slice(0, end);
  }

  normalizeAudio(samples) {
    // Find max amplitude
    let max = 0;
    for (let i = 0; i < samples.length; i++) {
      const abs = Math.abs(samples[i]);
      if (abs > max) max = abs;
    }
    
    // Normalize to 0.8 to avoid clipping
    if (max > 0.001) {
      const factor = 0.8 / max;
      const normalized = new Float32Array(samples.length);
      for (let i = 0; i < samples.length; i++) {
        normalized[i] = samples[i] * factor;
      }
      return normalized;
    }
    
    return samples;
  }

  highPassFilter(samples, cutoffFreq = 80) {
    // Simple high-pass filter to remove DC offset and low-frequency noise
    const RC = 1.0 / (2.0 * Math.PI * cutoffFreq);
    const dt = 1.0 / this.audioContext.sampleRate;
    const alpha = RC / (RC + dt);
    
    const filtered = new Float32Array(samples.length);
    filtered[0] = samples[0];
    
    for (let i = 1; i < samples.length; i++) {
      filtered[i] = alpha * (filtered[i - 1] + samples[i] - samples[i - 1]);
    }
    
    return filtered;
  }

  calculateRMS(samples) {
    const sum = samples.reduce((acc, sample) => acc + sample * sample, 0);
    return Math.sqrt(sum / samples.length);
  }

  resample(samples, fromRate, toRate) {
    if (fromRate === toRate) return samples;

    const ratio = fromRate / toRate;
    const newLength = Math.round(samples.length / ratio);
    const result = new Float32Array(newLength);

    for (let i = 0; i < newLength; i++) {
      const srcIndex = i * ratio;
      const srcIndexFloor = Math.floor(srcIndex);
      const srcIndexCeil = Math.min(srcIndexFloor + 1, samples.length - 1);
      const t = srcIndex - srcIndexFloor;
      
      result[i] = samples[srcIndexFloor] * (1 - t) + samples[srcIndexCeil] * t;
    }

    return result;
  }

  float32ToPCM16(float32Array) {
    const pcm16 = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return pcm16;
  }

  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  stop() {
    console.log('[AudioRecorder] 🛑 Stopping...');
    
    // Send any remaining audio before stopping (if not already sending)
    if (this.completeAudioBuffer.length > 0 && !this.isSending) {
      console.log('[AudioRecorder] 📦 Sending remaining buffered audio on stop');
      this.sendCompleteAudio();
    } else if (this.isSending) {
      console.log('[AudioRecorder] ⏳ Already sending audio, waiting...');
    } else {
      console.log('[AudioRecorder] ℹ️ No buffered audio to send');
    }
    
    this.isRecording = false;

    // Clear silence detection interval
    if (this.silenceCheckInterval) {
      clearInterval(this.silenceCheckInterval);
      this.silenceCheckInterval = null;
    }

    // Disconnect processor but keep AudioContext and MediaStream alive for next recording
    if (this.processor) {
      this.processor.disconnect();
      this.processor = null;
    }

    // Reset state for next recording
    this.audioBuffer = [];
    this.completeAudioBuffer = [];
    this.lastVoiceTime = null;
    this.hasDetectedVoice = false;
    this.isSending = false;
    
    console.log(`[AudioRecorder] ✅ Stopped (keeping mic and context alive for next recording)`);
  }

  cleanup() {
    console.log('[AudioRecorder] 🧹 Full cleanup - closing everything');
    
    this.isRecording = false;

    // Clear silence detection interval
    if (this.silenceCheckInterval) {
      clearInterval(this.silenceCheckInterval);
      this.silenceCheckInterval = null;
    }

    if (this.processor) {
      this.processor.disconnect();
      this.processor = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    this.audioBuffer = [];
    this.completeAudioBuffer = [];
    this.lastVoiceTime = null;
    this.hasDetectedVoice = false;
    this.isSending = false;
    
    console.log(`[AudioRecorder] ✅ Full cleanup complete`);
  }
}

/**
 * Audio Player for WebSocket Communication
 * Handles audio playback from server
 */
export class AudioPlayer {
  constructor(callbacks) {
    this.callbacks = callbacks;
    this.audioContext = null;
    this.audioQueue = [];
    this.isPlaying = false;
    this.nextStartTime = 0; // Track when the next chunk should start
    this.isInitialized = false; // Track initialization state
    
    // OPTIMIZED: Pre-initialize audio context for faster first chunk
    this.preInitialize();
  }

  async preInitialize() {
    try {
      // Pre-create audio context to avoid delay on first chunk
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.isInitialized = true;
      console.log(`[AudioPlayer] Pre-initialized with sample rate: ${this.audioContext.sampleRate}Hz`);
    } catch (error) {
      console.warn('[AudioPlayer] Pre-initialization failed:', error);
    }
  }

  async initialize() {
    if (this.isInitialized && this.audioContext) {
      // Already pre-initialized, just resume if needed
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      return;
    }

    // Fallback initialization if pre-init failed
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    this.isInitialized = true;

    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    console.log(`[AudioPlayer] Initialized with sample rate: ${this.audioContext.sampleRate}Hz`);
  }

  async playAudioChunk(base64Audio) {
    try {
      // OPTIMIZED: Faster initialization check
      await this.initialize();

      // OPTIMIZED: Direct decode without intermediate variables for speed
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Convert PCM16 to Float32 - no sample rate conversion, play as-is
      const pcm16 = new Int16Array(bytes.buffer);
      const float32 = new Float32Array(pcm16.length);
      for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / (pcm16[i] < 0 ? 0x8000 : 0x7FFF);
      }

      // Create audio buffer at 24kHz (OpenAI Realtime API PCM16 format is 24kHz)
      const audioBuffer = this.audioContext.createBuffer(
        1,
        float32.length,
        24000  // OpenAI's Realtime API outputs PCM16 at 24kHz
      );
      audioBuffer.getChannelData(0).set(float32);

      // Create and play source
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.audioContext.destination);
      
      if (!this.isPlaying) {
        this.isPlaying = true;
        this.callbacks.onPlaybackStateChange(true);
      }

      // OPTIMIZED: Calculate timing with minimal overhead
      const currentTime = this.audioContext.currentTime;
      const startTime = Math.max(currentTime, this.nextStartTime);
      this.nextStartTime = startTime + audioBuffer.duration;

      source.onended = () => {
        this.audioQueue = this.audioQueue.filter(s => s !== source);
        if (this.audioQueue.length === 0) {
          this.isPlaying = false;
          this.nextStartTime = 0; // Reset for next response
          this.callbacks.onPlaybackStateChange(false);
        }
      };

      // Start at scheduled time for seamless playback
      source.start(startTime);
      this.audioQueue.push(source);
      
      console.log(`[AudioPlayer] ⚡ Playing: ${audioBuffer.duration.toFixed(3)}s, queue=${this.audioQueue.length}`);

    } catch (error) {
      console.error('[AudioPlayer] Failed to play chunk:', error);
    }
  }

  stop() {
    console.log('╔═══════════════════════════════════════════════════════╗');
    console.log('║ [AudioPlayer] 🛑 INTERRUPTION - Stopping All Audio  ║');
    console.log(`║ - Queue length: ${String(this.audioQueue.length).padEnd(38)}║`);
    console.log(`║ - Next start time: ${String(this.nextStartTime.toFixed(3)).padEnd(33)}║`);
    console.log('╚═══════════════════════════════════════════════════════╝');
    
    // Stop all scheduled and playing audio sources
    let stoppedCount = 0;
    this.audioQueue.forEach(source => {
      try {
        source.stop();
        source.disconnect(); // Fully disconnect from audio graph
        stoppedCount++;
      } catch (e) {
        // Ignore if already stopped
      }
    });

    // Clear the queue completely
    this.audioQueue = [];
    this.nextStartTime = 0; // Reset timing for next response
    
    if (this.isPlaying) {
      this.isPlaying = false;
      this.callbacks.onPlaybackStateChange(false);
    }
    
    console.log(`[AudioPlayer] ✓ Stopped ${stoppedCount} audio sources, queue cleared, ready for new response`);
  }

  cleanup() {
    this.stop();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}
